import { ColunaDicionario, DicionarioSchema, TabelaDicionario } from 'src/app/services/relatorio-dinamico.service';

const RESERVADAS = new Set([
  'on', 'where', 'join', 'left', 'right', 'inner', 'full', 'outer', 'cross', 'natural', 'lateral', 'group', 'order', 'limit',
  'offset', 'union', 'except', 'intersect', 'having', 'window', 'fetch', 'for', 'as', 'and', 'or', 'using', 'select', 'set',
]);
const MAX_VALORES = 40;

let dicionario: DicionarioSchema = null;
let tabelas = new Map<string, TabelaDicionario>();
let registrado = false;

export function definirDicionarioSql(monaco: any, novo: DicionarioSchema): void {
  dicionario = novo;
  tabelas = new Map((novo?.tabelas || []).filter((t) => !t.bloqueada).map((t) => [t.nome, t] as [string, TabelaDicionario]));
  if (registrado || !monaco || !novo) {
    return;
  }
  registrado = true;
  monaco.languages.registerCompletionItemProvider('sql', {
    triggerCharacters: ['.'],
    provideCompletionItems: (model: any, position: any) => sugerir(monaco, model, position),
  });
  monaco.languages.registerHoverProvider('sql', {
    provideHover: (model: any, position: any) => explicar(model, position),
  });
}

function sugerir(monaco: any, model: any, position: any): any {
  if (!dicionario) {
    return { suggestions: [] };
  }
  const tipos = monaco.languages.CompletionItemKind;
  const palavra = model.getWordUntilPosition(position);
  const range = {
    startLineNumber: position.lineNumber,
    endLineNumber: position.lineNumber,
    startColumn: palavra.startColumn,
    endColumn: palavra.endColumn,
  };
  const antes = model.getLineContent(position.lineNumber).slice(0, palavra.startColumn - 1);
  const mapa = apelidos(model.getValue());
  const ponto = /([a-z_][a-z0-9_]*)\.$/i.exec(antes);
  if (ponto) {
    const tabela = resolver(ponto[1], mapa);
    return { suggestions: tabela ? colunas(tabela).map((c, i) => itemColuna(tipos, tabela, c, range, '0' + ordem(i))) : [] };
  }
  const sugestoes: any[] = [];
  new Set(mapa.values()).forEach((nome) => {
    const tabela = tabelas.get(nome);
    colunas(tabela).forEach((c, i) => sugestoes.push(itemColuna(tipos, tabela, c, range, '1' + ordem(i))));
  });
  tabelas.forEach((t) => {
    sugestoes.push({
      label: t.nome,
      kind: tipos.Class,
      detail: resumoTabela(t),
      documentation: { value: textoTabela(t) },
      insertText: t.nome,
      range,
      sortText: '2' + t.nome,
    });
  });
  return { suggestions: sugestoes };
}

function explicar(model: any, position: any): any {
  if (!dicionario) {
    return null;
  }
  const palavra = model.getWordAtPosition(position);
  if (!palavra) {
    return null;
  }
  const nome = palavra.word.toLowerCase();
  const range = {
    startLineNumber: position.lineNumber,
    endLineNumber: position.lineNumber,
    startColumn: palavra.startColumn,
    endColumn: palavra.endColumn,
  };
  const antes = model.getLineContent(position.lineNumber).slice(0, palavra.startColumn - 1);
  const mapa = apelidos(model.getValue());
  const ponto = /([a-z_][a-z0-9_]*)\.$/i.exec(antes);
  if (ponto) {
    const tabela = resolver(ponto[1], mapa);
    const coluna = tabela && colunas(tabela).find((c) => c.nome === nome);
    return coluna ? { range, contents: [{ value: textoColuna(tabela, coluna) }] } : null;
  }
  if (mapa.has(nome) || tabelas.has(nome)) {
    const tabela = tabelas.get(mapa.get(nome) || nome);
    return tabela ? { range, contents: [{ value: textoTabela(tabela) }] } : null;
  }
  for (const nomeTabela of new Set(mapa.values())) {
    const tabela = tabelas.get(nomeTabela);
    const coluna = colunas(tabela).find((c) => c.nome === nome);
    if (coluna) {
      return { range, contents: [{ value: textoColuna(tabela, coluna) }] };
    }
  }
  return null;
}

function apelidos(texto: string): Map<string, string> {
  const mapa = new Map<string, string>();
  const anotar = (tabela: string, apelido: string) => {
    const t = tabela.toLowerCase();
    if (!tabelas.has(t)) {
      return;
    }
    mapa.set(t, t);
    const a = (apelido || '').toLowerCase();
    if (a && !RESERVADAS.has(a)) {
      mapa.set(a, t);
    }
  };
  const depoisDeFrom = /\b(?:from|join)\s+(?:"?public"?\.)?"?([a-z_][a-z0-9_]*)"?(?:\s+(?:as\s+)?([a-z_][a-z0-9_]*))?/gi;
  const depoisDeVirgula = /,\s*(?:"?public"?\.)?"?([a-z_][a-z0-9_]*)"?\s+(?:as\s+)?([a-z_][a-z0-9_]*)/gi;
  let m: RegExpExecArray;
  while ((m = depoisDeFrom.exec(texto))) {
    anotar(m[1], m[2]);
  }
  while ((m = depoisDeVirgula.exec(texto))) {
    anotar(m[1], m[2]);
  }
  return mapa;
}

function resolver(nome: string, mapa: Map<string, string>): TabelaDicionario {
  const chave = nome.toLowerCase();
  return tabelas.get(mapa.get(chave) || chave) || null;
}

function colunas(tabela: TabelaDicionario): ColunaDicionario[] {
  return (tabela?.colunas || []).filter((c) => !c.bloqueada);
}

function itemColuna(tipos: any, tabela: TabelaDicionario, c: ColunaDicionario, range: any, ordem: string): any {
  return {
    label: c.nome,
    kind: c.enum ? tipos.Enum : c.fk ? tipos.Reference : tipos.Field,
    detail: `${tabela.nome} · ${c.tipo}${c.fk ? ' → ' + c.fk.tabela : ''}`,
    documentation: { value: textoColuna(tabela, c) },
    insertText: c.nome,
    range,
    sortText: ordem,
  };
}

function resumoTabela(t: TabelaDicionario): string {
  const entidades = t.entidades?.length ? t.entidades.join(', ') + ' · ' : '';
  return `${entidades}${colunas(t).length} colunas`;
}

function textoTabela(t: TabelaDicionario): string {
  const partes = [`**${t.nome}** · ${resumoTabela(t)}`];
  if (t.descricao) {
    partes.push(t.descricao);
  }
  return partes.join('\n\n');
}

function textoColuna(t: TabelaDicionario, c: ColunaDicionario): string {
  const partes = [`**${t.nome}.${c.nome}** · ${c.tipo}${c.pk ? ' · chave primária' : ''}${c.obrigatoria ? ' · obrigatória' : ''}`];
  if (c.campo && t.entidades?.length) {
    partes.push(`Campo \`${t.entidades[0]}.${c.campo}\``);
  }
  if (c.fk) {
    partes.push(`Aponta para \`${c.fk.tabela}.${c.fk.coluna}\``);
  }
  if (c.descricao) {
    partes.push(c.descricao);
  }
  const valores = c.enum && dicionario?.enums?.[c.enum.nome]?.valores;
  if (valores?.length) {
    const ordinal = c.enum.gravadoComo === 'ordinal';
    const linhas = valores.slice(0, MAX_VALORES).map((v) => {
      const gravado = ordinal ? String(v.ordinal) : `'${v.nome}'`;
      const rotulo = v.rotulo || (ordinal ? v.nome : '');
      return `- \`${gravado}\`${rotulo ? ' ' + rotulo : ''}`;
    });
    if (valores.length > MAX_VALORES) {
      linhas.push(`- e mais ${valores.length - MAX_VALORES}`);
    }
    partes.push(`${ordinal ? 'Gravado como número' : 'Gravado como texto'} (${c.enum.nome}):\n${linhas.join('\n')}`);
  }
  return partes.join('\n\n');
}

function ordem(i: number): string {
  return String(i).padStart(4, '0');
}
